import { getFrontendConfigValue } from '@arnold/common';
import { clone } from 'ramda';
import { useEffect } from 'react';
import { TFunction } from 'react-i18next';
import {
  MutationImportTopicArgs,
  QuestionDefinitionInput,
  QuestionDefinitionTranslationInput,
  QuestionType,
  QuestionType as QuestionTypeFromHooks,
  RuleOperator,
  TopicDetailQuery,
  TopicGroupDetailQuery,
} from '../../generated/hooks';
import { COMMENT_TYPE } from './constants';
import { OptionControlData } from './controls/OptionControl';
import {
  CustomConnection,
  DataNode,
  DataNodeWithDimensions,
  NodeData,
  OutputKey,
  normalizeSingleOutput,
} from './dataNode';
import { CustomNodeEditor, processData, updatePositionInNodeData } from './rete';

type topicDetailQuestions = NonNullable<TopicGroupDetailQuery['topicGroup']['lastValidTopic']>['allQuestions'];
type topicDetailComments = NonNullable<TopicGroupDetailQuery['topicGroup']['lastValidTopic']>['commentGroups'];

const QUESTION_TYPE_FROM_HOOKS_MAPPING = {
  SelectQuestionDefinition: QuestionTypeFromHooks.Select,
  ScaleQuestionDefinition: QuestionTypeFromHooks.Scale,
  FreeTextQuestionDefinition: QuestionTypeFromHooks.Freetext,
  EndingQuestionDefinition: QuestionTypeFromHooks.Ending,
  CommentQuestionDefinition: QuestionTypeFromHooks.Tell,
  PersonMultiselectQuestionDefinition: QuestionTypeFromHooks.PersonMultiselect,
  SmsContactQuestionDefinition: QuestionTypeFromHooks.SmsContact,
  EmailContactQuestionDefinition: QuestionTypeFromHooks.EmailContact,
};

const trimRuleOrigValue = (origValue: string) => {
  return origValue.replace(/^\[+|\]+$/g, '');
};

type QuestionRange = {
  min: number;
  max: number;
};

const parseRange = (rawSelectedRange?: string): QuestionRange => {
  const selectedRange = rawSelectedRange && rawSelectedRange.split('-');
  const range = selectedRange
    ? {
        min: parseInt(selectedRange[0], 10),
        max: parseInt(selectedRange[selectedRange.length > 1 ? 1 : 0], 10),
      }
    : {
        min: 1,
        max: 1,
      };
  return range;
};

export const getOutputs = (question: topicDetailQuestions[0]) => {
  const outputs: { [key: string]: number | null } = {};

  if (question.rules.length === 1 && !['RangeRule', 'ArrayRule'].includes(question.rules[0].__typename ?? '')) {
    return { 'opt-0': question.rules[0].nextQuestion.index ?? null };
  }

  if (
    question.rules.length === 0 &&
    !['EndingQuestionDefinition', 'SelectQuestionDefinition'].includes(question.__typename as string)
  ) {
    // if there are no rules (not connected to anything) and it is not the ending quesion
    // it should to have an output
    return { 'opt-0': null };
  }

  const translation = question.translations.find((translation) => translation.isDefault);
  const countOfChoices = translation?.choices?.length || 0;
  for (let index = 0; index < countOfChoices; index++) {
    // create outputs for all choices - it is not enough to do it for each rule (in the next step)
    // coz outputs without connection do not have rules
    const outputKey = `opt-${index}`;
    outputs[outputKey] = null;
  }

  [...question.rules]
    .sort((q1, q2) => q2.order - q1.order)
    .forEach((rule) => {
      const trimmedOrigValue = trimRuleOrigValue(rule.origValue);
      if (trimmedOrigValue === '') {
        const countOfChoices = translation?.choices?.length || 1;
        for (let index = 0; index < countOfChoices; index++) {
          const outputKey = `opt-${index}`; // option number is paired onto translation.choice option number
          outputs[outputKey] = rule.nextQuestion.index ?? null;
        }
      } else {
        const outputKey = `opt-${Number.parseInt(trimmedOrigValue, 10)}`;
        outputs[outputKey] = rule.nextQuestion.index ?? null;
      }
    });

  // e.g. a SCALE saved with one rule per choice must still render a single output
  return normalizeSingleOutput(QUESTION_TYPE_FROM_HOOKS_MAPPING[question.__typename!], outputs);
};

const getAnswersToHighlight = (question: topicDetailQuestions[0]) => {
  const answersToHighlight: { [key: OutputKey]: number } = {};
  const translation = question.translations.find((translation) => translation.isDefault);
  const countOfChoices = translation?.choices?.length || 0;
  for (let choiceIndex = 0; choiceIndex < countOfChoices; choiceIndex++) {
    const outputKey: OutputKey = `opt-${choiceIndex}`;
    answersToHighlight[outputKey] = (question.answersToHighlight || []).filter(
      (index) => index === choiceIndex.toString(),
    ).length;
  }

  return answersToHighlight;
};

export const topicDetailToNodes = (
  allQuestions: topicDetailQuestions,
  languageCode: string,
  startingQuestionId: string,
  commentGroups: topicDetailComments,
  topicId: string,
  search?: string,
): NodeData[] => {
  const questionNodes = allQuestions.map((question) => {
    const allTranslations = question.translations;
    const primaryTranslation = question.translations.find((translation) => translation.isDefault);
    const translation = question.translations.find((translation) => translation.language.code === languageCode);
    const choices =
      primaryTranslation?.choices && primaryTranslation.choices.length > 0
        ? primaryTranslation.choices.reduce((acc, choice, index) => {
            const translatedChoice = translation?.choices?.[index] || '';
            return { ...acc, [`opt-${index}`]: translatedChoice };
          }, {})
        : {};
    const primaryStatements = primaryTranslation?.statements ?? [];
    const translatedStatements = translation?.statements ?? [];
    const statements = primaryStatements.map((_, idx) => translatedStatements?.[idx] || '');

    let questionType = QUESTION_TYPE_FROM_HOOKS_MAPPING[question.__typename!];
    const range = parseRange(question.selectedRange);
    if (questionType === QuestionTypeFromHooks.Select && !(range.max === 1 && range.min === 1)) {
      questionType = QuestionTypeFromHooks.Multiselect;
    }
    const outputs = getOutputs(question);
    return {
      questionIndex: question.index ?? generateIndex(),
      questionText: translation?.text || '',
      isRequired: !question.optional,
      metrics: question.metrics ?? [],
      isQuestionTrigger: question.isQuestionTrigger,
      randomizeOrder: question.__typename === 'ScaleQuestionDefinition' ? question.randomizeOrder ?? false : false,
      isRankingScale: question.__typename === 'ScaleQuestionDefinition' ? question.isRankingScale ?? false : false,
      showEscapeOption:
        question.__typename === 'ScaleQuestionDefinition' ? question.showEscapeOption ?? true : undefined,
      choices,
      statements,
      type: questionType,
      range,
      answersToHighlight: getAnswersToHighlight(question),
      allTranslations,
      input: startingQuestionId !== question.id,
      outputs,
      reportOrder: question.reportOrder ?? null,
      position: [question.positionX ?? null, question.positionY ?? null],
      originalQuestionDefinition: question,
      topicId,
    };
  });

  const commentNodes =
    commentGroups?.map((commentGroup) => {
      return {
        type: COMMENT_TYPE as QuestionType | typeof COMMENT_TYPE,
        questionIndex: Number.parseInt(commentGroup.id, 10),
        questionText: '',
        isRequired: false,
        metrics: [],
        isQuestionTrigger: false,
        choices: {},
        input: false,
        outputs: {},
        reportOrder: null,
        position: [commentGroup.positionX ?? null, commentGroup.positionY ?? null],
        comments: commentGroup.comments || undefined,
        topicId,
        search,
        commentGroupId: commentGroup.id,
      };
    }) || [];

  return [...questionNodes, ...commentNodes];
};

/**
 * This will transform the NodeData to be used in the save mutation
 */
export const transformNodesForMutation = (
  editor: CustomNodeEditor,
  topicName: string,
  topicGroupId: string,
  languageCode: string,
  languageId: string,
  defaultTopicLanguageCode: string,
  isTesting: boolean,
  topicGroupDescription: string | undefined,
  isValid: boolean,
  savingNewLanguage = false,
  topicId: string,
  questionOrderManuallyModified: boolean,
  questionOrderModified: boolean,
  translateViaAi: boolean,
): MutationImportTopicArgs => {
  const isDefaultLanguage = defaultTopicLanguageCode === languageCode;
  const nodes = editor.getNodes();
  const connections = editor.getConnections();
  updateNodeDataBasedOnConnections(nodes, connections);
  // put the starting question (node without input) as the first question (needed for correct import of startingQuestion in some old topics)
  nodes.sort((a, b) => (a.data.input === false ? -1 : b.data.input === false ? 1 : 0));
  const transformedQuestions = nodes
    .filter((node) => node.data.type !== COMMENT_TYPE)
    .map((node, i): QuestionDefinitionInput => {
      const nodePosition = editor.areaPlugin.nodeViews.get(node.id)?.position;
      const type = node.data.type;
      // Order of choices is controlled by index in option control
      const controlKeys = Object.values(node.controls)
        .filter((c) => c instanceof OptionControlData)
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
        .map((x) => (x as OptionControlData).key) as OutputKey[];

      const outputKeys: OutputKey[] = controlKeys.length
        ? controlKeys
        : node.data.outputs
          ? (Object.keys(node.data.outputs) as OutputKey[])
          : [];
      const choices = controlKeys.map((key) => node.data.choices[key] || '');

      const rules = outputKeys
        .map((outputKey) => {
          const nextQuestionIndex = node.data.outputs?.[outputKey];
          const indexOfOption = controlKeys.findIndex((key) => key === outputKey);
          const optionNo =
            type !== COMMENT_TYPE && [QuestionType.Select, QuestionType.Multiselect].includes(type)
              ? indexOfOption !== -1
                ? indexOfOption.toString()
                : outputKey.replace('opt-', '') // remove the opt- prefix
              : '';
          return {
            nextQuestionIndex: nextQuestionIndex !== null ? nextQuestionIndex?.toString() : null, // needs to be string
            rule: QuestionType.Multiselect === type ? `[${optionNo}]` : optionNo,
            ruleOperator: QuestionType.Multiselect === type ? RuleOperator.Inany : null,
          };
        })
        .filter((rule) => rule.nextQuestionIndex)
        .reverse();

      const answersToHighlight = controlKeys
        .map((key, index) =>
          Array.from(Array(node.data.answersToHighlight?.[key] || 0).keys()).map(() => index.toString()),
        )
        .flat();

      const statements = node.data.statements || [];

      return {
        index: node.data.questionIndex,
        positionX: nodePosition?.x ? Math.floor(nodePosition.x) : null,
        positionY: nodePosition?.y ? Math.floor(nodePosition.y) : null,
        type: type as QuestionType,
        selectedRange: node.data.range,
        rules,
        translations: [
          // saving existing nodes
          ...(node.data.originalQuestionDefinition?.translations.map((trans) => ({
            isDefault: trans.language.code === defaultTopicLanguageCode,
            languageId: trans.language.id,
            text:
              savingNewLanguage && trans.language.code === languageCode
                ? trans.text
                : trans.language.code === languageCode
                  ? node.data.questionText || ''
                  : trans.text,
            choices:
              savingNewLanguage && trans.language.code === languageCode
                ? trans.choices
                : trans.language.code === languageCode
                  ? choices
                  : trans.choices,
            statements:
              savingNewLanguage && trans.language.code === languageCode
                ? trans.statements
                : trans.language.code === languageCode
                  ? statements
                  : trans.statements,
          })) || []),
          // saving new language
          savingNewLanguage &&
            !translateViaAi &&
            !node.data.originalQuestionDefinition?.translations.find(
              (translation) => translation.language.id === languageId,
            ) && {
              isDefault: false,
              languageId,
              text: node.data.originalQuestionDefinition?.translations.find((trans) => trans.isDefault)!.text || '',
              choices: node.data.originalQuestionDefinition?.translations.find((trans) => trans.isDefault)!.choices,
              statements: (node.data.originalQuestionDefinition?.translations.find((trans) => trans.isDefault) as any)
                ?.statements,
            },
          // saving new nodes that were not saved before
          !savingNewLanguage &&
            !node.data.originalQuestionDefinition?.translations.some(
              (trans) => trans.language.code === languageCode,
            ) && {
              isDefault: isDefaultLanguage,
              text: node.data.questionText || '',
              languageId,
              choices,
              statements,
            },
        ].filter((item) => item) as QuestionDefinitionTranslationInput[],
        answersToHighlight,
        optional: type === QuestionType.Freetext && !node.data.isRequired,
        isFirstInSectionWithNumber: node.data.originalQuestionDefinition?.isFirstInSectionWithNumber,
        reportIndex: node.data.originalQuestionDefinition?.reportIndex,
        reportOrder: node.data.reportOrder,
        randomizeOrder: node.data.randomizeOrder ?? false,
        isRankingScale: type === QuestionType.Scale ? node.data.isRankingScale ?? false : null,
        showEscapeOption: type === QuestionType.Scale ? node.data.showEscapeOption ?? true : null,
      };
    });

  return {
    topic: {
      topicName,
      description: topicGroupDescription,
      languageId,
      language: languageCode,
      topicGroupId,
      isApproved: true,
      isTesting,
      isValid,
      previousTopicId: topicId,
    },
    questions: transformedQuestions,
    questionOrderManuallyModified,
    questionOrderModified,
    translateViaAi,
  };
};

export const clearEditor = (editor: CustomNodeEditor, t: TFunction, lng: string, onDelete: (id: string) => void) => {
  const secondNode: NodeData = {
    type: QuestionType.Ending,
    questionIndex: generateIndex(),
    questionText: t('lastNodeText', {
      lng,
    }),
    isRequired: false,
    choices: {},
    input: true,
    metrics: [],
    isQuestionTrigger: false,
    outputs: {},
    reportOrder: 2,
    position: [] as any,
  };
  const firstNode: NodeData = {
    type: QuestionType.Tell,
    questionIndex: generateIndex(),
    questionText: t('firstNodeText', {
      lng,
    }),
    isRequired: false,
    choices: {},
    input: false,
    metrics: [],
    isQuestionTrigger: false,
    outputs: { 'opt-0': secondNode.questionIndex },
    reportOrder: 1,
    position: [] as any,
  };
  const commentNodes = editor.getNodes().filter((node) => node.data.type === COMMENT_TYPE);
  commentNodes.forEach((node) => {
    if (node.data.commentGroupId) {
      onDelete(node.data.commentGroupId);
    }
  });
  processData([firstNode, secondNode], editor, t);
};

/**
 * @returns number between 0 and 2147483647 which is the max of int in DB
 */
export function generateIndex(): number {
  return Math.floor(Math.random() * 2147483647);
}
function pruneOrphanOutputs(node: DataNode): void {
  if (!node.data.outputs || Object.keys(node.data.choices).length === 0) {
    return;
  }
  Object.keys(node.data.outputs).forEach((key) => {
    if (!(key in node.data.choices)) {
      delete node.data.outputs![key as OutputKey];
    }
  });
}

/**
 * based on how the nodes are connected
 */
export function updateNodeDataBasedOnConnections(
  nodes: DataNode[],
  connections: CustomConnection<DataNodeWithDimensions>[],
): void {
  nodes.forEach((node) => {
    pruneOrphanOutputs(node);
    node.data.outputs &&
      Object.keys(node.data.outputs).forEach((key) => {
        // remove old connection from node data
        if (node.data.outputs) {
          node.data.outputs[key as OutputKey] = null;
        }
      });
  });
  connections.forEach((con) => {
    const sourceNode = nodes.find((node) => node.id === con.source);
    const targetNode = nodes.find((node) => node.id === con.target);
    if (sourceNode == null)
      throw new Error('Node not find but connection still exists. connection.source=' + con.source);
    if (targetNode == null)
      throw new Error('Node not find but connection still exists. connection.source=' + con.target);
    // save new connections to node data
    const outputKey = con.sourceOutput as OutputKey;
    if (
      sourceNode.data.outputs &&
      (Object.keys(sourceNode.data.choices).length === 0 || outputKey in sourceNode.data.choices)
    ) {
      sourceNode.data.outputs[outputKey] = targetNode.data.questionIndex;
    }
  });
}

export function syncTopicEditorSocketLocks(editor: CustomNodeEditor): void {
  const nodes = editor.getNodes() as DataNode[];
  const connections = editor.getConnections();
  updateNodeDataBasedOnConnections(nodes, connections);

  const lockEditQuestionIndices = nodes.filter((n) => n.lockEdit).map((n) => n.data.questionIndex) as readonly number[];

  for (const node of nodes) {
    let inputLockedPair = false;
    if (node.lockEdit) {
      const incoming = connections.filter((c) => c.target === node.id);
      inputLockedPair =
        incoming.length > 0 &&
        incoming.every((c) => {
          const src = nodes.find((n) => n.id === c.source);
          return Boolean(src?.lockEdit);
        });
    }
    node.topicEditorLockEditQuestionIndices = lockEditQuestionIndices;
    node.topicEditorInputSocketLockedPair = inputLockedPair;
  }
}

export const getTGAccessHeaderContext = (search: string) => {
  const parsedSearch = new URLSearchParams(search);
  const accessKey = parsedSearch.get('accessKey');

  if (!accessKey) {
    return undefined;
  }

  const mutationContext = {
    headers: {
      'tg-access-key': accessKey,
    },
  };
  return mutationContext;
};
/**
 * On window close show alert dialog telling the user that if he exits the changes will not be saved
 */
export function useWarnUserWhenClosingTab(getIsDirty: () => boolean) {
  useEffect(() => {
    const alertUser = (event: BeforeUnloadEvent) => {
      const isDirty = getIsDirty();
      if (!isDirty) return undefined;
      event.returnValue = '';
      return '';
    };
    if (!['cypress'].includes(getFrontendConfigValue('ENVIRONMENT'))) {
      window.addEventListener('beforeunload', alertUser);
    }
    return () => {
      if (!['cypress'].includes(getFrontendConfigValue('ENVIRONMENT'))) {
        window.removeEventListener('beforeunload', alertUser);
      }
    };
  }, [getIsDirty]);
}

export const getNodesToCompare = (editor: CustomNodeEditor): NodeData[] => {
  const nodes = editor.getNodes().filter((node) => node.data.type !== COMMENT_TYPE);
  updateNodeDataBasedOnConnections(nodes, editor.getConnections());
  updatePositionInNodeData(editor, nodes);
  return nodes.map((node) => clone(node.data));
};

export const cloneGraphNodes = (nodes: NodeData[]): NodeData[] => clone(nodes);

function pruneOrphanOutputsOnNodeData(data: NodeData): void {
  if (!data.outputs || Object.keys(data.choices).length === 0) {
    return;
  }
  Object.keys(data.outputs).forEach((key) => {
    if (!(key in data.choices)) {
      delete data.outputs![key as OutputKey];
    }
  });
}

export const getFullGraphSnapshot = (editor: CustomNodeEditor): NodeData[] => {
  const nodes = editor.getNodes();
  const connections = editor.getConnections();
  const indexByNodeId = new Map(nodes.map((node) => [node.id, node.data.questionIndex]));
  const snapshots = nodes.map((node) => clone(node.data));
  const snapshotByIndex = new Map(snapshots.map((data) => [data.questionIndex, data]));

  snapshots.forEach((data) => {
    pruneOrphanOutputsOnNodeData(data);
    if (data.outputs) {
      Object.keys(data.outputs).forEach((key) => {
        data.outputs![key as OutputKey] = null;
      });
    }
  });

  connections.forEach((con) => {
    const sourceIndex = indexByNodeId.get(con.source);
    const targetIndex = indexByNodeId.get(con.target);
    if (sourceIndex == null || targetIndex == null) {
      return;
    }
    const sourceSnapshot = snapshotByIndex.get(sourceIndex);
    const targetSnapshot = snapshotByIndex.get(targetIndex);
    if (!sourceSnapshot?.outputs || !targetSnapshot) {
      return;
    }
    const outputKey = con.sourceOutput as OutputKey;
    if (Object.keys(sourceSnapshot.choices).length === 0 || outputKey in sourceSnapshot.choices) {
      sourceSnapshot.outputs[outputKey] = targetSnapshot.questionIndex;
    }
  });

  nodes.forEach((node) => {
    const snapshot = snapshotByIndex.get(node.data.questionIndex);
    if (!snapshot) {
      return;
    }
    const nodePosition = editor.areaPlugin.nodeViews.get(node.id)?.position;
    snapshot.position = [
      nodePosition ? Math.floor(nodePosition.x) : null,
      nodePosition ? Math.floor(nodePosition.y) : null,
    ];
  });

  return snapshots;
};

/**
 * Compares reportOrder only for questions that exist in both the baseline snapshot and the current graph.
 */
export const reportOrdersMatchInitialSnapshot = (
  editor: CustomNodeEditor,
  initialNodes: NodeData[] | undefined | null,
): boolean => {
  if (initialNodes == null) return true;
  const currentNodes = getNodesToCompare(editor);
  const initialQuestions = initialNodes.filter((n) => n.type !== COMMENT_TYPE);
  const currentQuestions = currentNodes.filter((n) => n.type !== COMMENT_TYPE);
  const currentByIndex = new Map(currentQuestions.map((n) => [n.questionIndex, n.reportOrder]));
  for (const n of initialQuestions) {
    if (!currentByIndex.has(n.questionIndex)) continue;
    if (currentByIndex.get(n.questionIndex) !== n.reportOrder) return false;
  }
  return true;
};

export const nodesEqual = (initialNodes: NodeData[] | undefined, editor: CustomNodeEditor, isTesting = false) => {
  if (initialNodes == null) return false;
  const currentNodes = getNodesToCompare(editor);
  if (initialNodes.every((node) => node.position[0] == null)) {
    // if all initial nodes have null position, it means that this topic was never saved by the Topic editor
    // in that case the position of the new nodes should not be compared, otherwise it would ask to save without any changes
    [...initialNodes, ...currentNodes].forEach((node) => {
      node.position = [null];
    });
  }
  if (isTesting) {
    [...initialNodes, ...currentNodes].forEach((node) => {
      node.position = [null];
      node.reportOrder = null;
    });
  }
  // Ramda does not work here for some unknown reason
  return JSON.stringify(initialNodes) === JSON.stringify(currentNodes);
};

/**
 * Topic detail can be corrupted / damaged and this function tries to fix it and is successful in most cases
 */
export const fixTopicDetail = (
  topicDetail: TopicDetailQuery['topicDetail'] | NonNullable<TopicGroupDetailQuery['topicGroup']['lastValidTopic']>,
) => {
  let somethingChanged = false;
  const clonedTopic = clone(topicDetail); // topicDetail is readonly
  const questions = clonedTopic.allQuestions;
  // In some weird topics there is no default translation, so we need to pick the first one that exists as the default
  questions.forEach((question) => {
    const haveDefaultTranslation = question.translations.some((translation) => translation.isDefault);
    if (!haveDefaultTranslation && question.translations[0]) {
      question.translations[0].isDefault = true;
      somethingChanged = true;
      console.warn(
        `Default translation was missing and needed to be added to the question with index ${question.index}`,
      );
    }
  });

  // In other weird topics there is a starting question id that is wrong and points to a question in the middle
  // In this case guess the real starting question which is successful in most cases where the topic is valid and fully connected
  const startingQuestionId = clonedTopic.startingQuestion.id;
  const startingQuestionIsInTheRulesOfOtherQuestions = questions.some((question) =>
    question.rules.some((rule) => rule.nextQuestion.id === startingQuestionId),
  );
  if (startingQuestionIsInTheRulesOfOtherQuestions) {
    // find question that is not in any rules of other questions - it should be the starting question (this might not be 100% correct if the topic is not valid and connected together)
    const startingQuestion = questions.find(
      (question) =>
        question.rules.length > 0 &&
        !questions.some((otherQuestion) => otherQuestion.rules.some((rule) => rule.nextQuestion.id === question.id)),
    );
    if (startingQuestion) {
      clonedTopic.startingQuestion = startingQuestion;
      somethingChanged = true;
      console.warn('Starting question id was wrong and was changed to: ', startingQuestion.id);
    }
  }

  if (somethingChanged) {
    return clonedTopic;
  }
  return topicDetail;
};

export type TranslationStatus = 'ok' | 'same_as_source' | 'source_changed' | 'missing';

/**
 * Determines the translation status of a node for the given language.
 *
 * - 'missing'        — no translation exists for the current language
 * - 'same_as_source' — translation was saved but never reviewed (e.g. from AI batch translate)
 * - 'source_changed' — the source language text was changed after this translation was saved
 * - 'ok'             — translation is up to date
 *
 * Returns 'ok' when viewing the default (source) language.
 *
 * Primary source of truth is the DB `translationStatus` field.
 * Falls back to text comparison for legacy records without DB status.
 */
export const getNodeTranslationStatus = (
  originalQuestionDefinition: NodeData['originalQuestionDefinition'],
  languageCode: string,
): TranslationStatus => {
  if (!originalQuestionDefinition) return 'ok';

  const defaultTranslation = originalQuestionDefinition.translations?.find((t) => t.isDefault);
  if (!defaultTranslation) return 'ok';

  // Viewing the source language — no warning needed
  if (defaultTranslation.language?.code === languageCode) return 'ok';

  const currentTranslation = originalQuestionDefinition.translations?.find((t) => t.language?.code === languageCode);

  if (!currentTranslation || !currentTranslation.text) return 'missing';

  return currentTranslation.translationStatus ?? 'ok';
};
